/**
 * @voltxampere/engine — Inductor AC stamp (Sprint 20)
 *
 * In frequency domain `V_L(jω) = jωL · I_L(jω)`. We keep the same
 * MNA augmentation as DC: the inductor reserves a `["vsource"]`
 * augmented row carrying the branch current `I_L`. The KCL columns
 * stay `±1` real-valued (current flow into / out of the terminal
 * nets); the constraint row's KVL coefficient on `I_L` becomes the
 * complex `-jωL` impedance term:
 *
 *     V_a - V_b - jωL · I_L = 0
 *
 *  Y[a, k] = +1 + 0j         ← KCL: I_L leaves node a
 *  Y[b, k] = -1 + 0j         ← KCL: I_L enters node b
 *  Y[k, a] = +1 + 0j         ← KVL: V_a coefficient
 *  Y[k, b] = -1 + 0j         ← KVL: V_b coefficient
 *  Y[k, k] =  0 - jωL         ← KVL: -jωL · I_L
 *  rhs[k]  =  0 + 0j          ← no source on the inductor branch
 *
 * `initialCurrent` is recognised by the schema for transient warmup
 * but ignored at AC.
 *
 * Copyright © 2026 360 Studio
 * Licensed under MIT (see LICENSE file in repo root)
 */
import { StampParameterError } from "@voltxampere/shared";
import { getNumericParam, getTerminalNetId } from "../terminal-utils.js";
export const inductorACStamp = (ctx, component) => {
    const l = getNumericParam(component, "inductance");
    if (!(l > 0) || !Number.isFinite(l)) {
        throw new StampParameterError(`inductorACStamp: component "${component.id}" has non-positive inductance ${String(l)}`, {
            meta: {
                stampType: "inductor",
                parameter: "inductance",
                received: l,
                expected: "> 0 and finite",
                componentId: component.id,
            },
        });
    }
    const omegaL = ctx.omega * l;
    const a = ctx.nodeIndex(getTerminalNetId(component, "p1"));
    const b = ctx.nodeIndex(getTerminalNetId(component, "p2"));
    // Sprint 24 B1 — vSourceIndex returns the absolute MNA row.
    const k = ctx.vSourceIndex(component.id);
    // KCL columns — current routing.
    ctx.addY(a, k, 1, 0);
    ctx.addY(b, k, -1, 0);
    // KVL row — voltage constraint.
    ctx.addY(k, a, 1, 0);
    ctx.addY(k, b, -1, 0);
    // -jωL on the augmented diagonal (the constraint reads
    //  V_a - V_b - jωL · I_L = 0, so the I_L coefficient is -jωL).
    ctx.addY(k, k, 0, -omegaL);
};
//# sourceMappingURL=inductor.js.map